import React, { useEffect } from "react";
import { faFilePdf } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Info } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { useHistory, useParams } from "react-router-dom";
import {
  CfcContentWrapper,
  CfcGeneralContainer,
  CfcMainColumn,
  CfcPageTitle,
  CfcReportButton,
  CfcResultCard,
  CfcResultCardTitle,
  CfcResultStatusRow,
  CfcSubtitleWrapper,
  CfcTitleWrapper,
  CfcWrapper,
  CfcBackToResults,
  CfcSupportColumn,
} from "./Cfc.styles";
import { GetTypographyContent } from "../../components/shared/Typography/Typography";
import { DetailedResultsViewReportButtonWrapper } from "../../components/DetailedResults/DetailedResults.styles";
import { DetailedResultsExpert } from "../../components/DetailedResults/DetailedResultsExpert";
import { DetailedResultsFAQ } from "../../components/DetailedResults/DetailedResultsFAQ";
import { getSpecificCfc, getUserAuth } from "../../recoil/selectors";
import { LANDING_PAGE_URL } from "../../utils/urlConstants";
import { BackIcon } from "../../assets/iconComponents/BackIcon";
import { SendActionType } from "../../components/shared/Services/SharedServices";
import { GetCfcInfo } from "../../components/shared/Typography/types";
import { useReportDownload } from "../../utils/hooks/useReportDownload";

const CfcPage = (): React.ReactElement => {
  const { requisitionID } = useParams<{ requisitionID: string }>();
  const cfcReport = useRecoilValue(getSpecificCfc(requisitionID));
  const history = useHistory();
  const auth = useRecoilValue(getUserAuth);
  const { downloadReport } = useReportDownload();
  const { t } = useTranslation();

  const status = cfcReport?.status;
  const summary = cfcReport?.summary;
  const resultAvailable = Boolean(
    status &&
    status !== "Not Ordered" &&
    status !== "Processing" &&
    status !== "Ordered",
  );

  useEffect(() => {
    const sendUIAction = async () => {
      if (!auth?.access_token || !cfcReport || !status) {
        return;
      }
      try {
        await SendActionType("ViewCfcPage", requisitionID, auth?.access_token, {
          result_available: String(resultAvailable),
          report_summary: summary || "",
        });
      } catch (e) {
        return undefined;
      }
    };
    sendUIAction();
  }, []);

  useEffect(() => {
    if (auth && (!cfcReport || !status)) {
      history.push(LANDING_PAGE_URL);
    }
  }, [cfcReport, status, auth, history]);

  const reportDisplay = GetCfcInfo(status);
  const reportDisplayText =
    reportDisplay.display === "See Report"
      ? t("cfc.status.seeReport")
      : reportDisplay.display;

  const handleReportDownload = () => {
    if (cfcReport?.id) {
      downloadReport(cfcReport.id, "cfc");
    }
  };

  const pageHeader = (
    <>
      <CfcBackToResults
        type="button"
        onClick={() => history.push(LANDING_PAGE_URL)}
      >
        <BackIcon />
        {GetTypographyContent({
          content: t("cfc.actions.backToTests"),
          size: "medium",
          textColor: "redMedium",
        })}
      </CfcBackToResults>
      <CfcTitleWrapper>
        <CfcPageTitle>{t("cfc.title")}</CfcPageTitle>
        <CfcSubtitleWrapper>
          {GetTypographyContent({
            content: t("cfc.subtitle"),
            size: "medium",
            textColor: "greyMedium",
          })}
          <Info aria-hidden />
        </CfcSubtitleWrapper>
      </CfcTitleWrapper>
    </>
  );

  const resultCard = (
    <CfcResultCard>
      <CfcResultCardTitle>{t("cfc.cardTitle")}</CfcResultCardTitle>
      <CfcResultStatusRow>
        <div>{reportDisplay.icon}</div>
        {GetTypographyContent({
          content: reportDisplayText,
          size: "large",
          textColor: "greyMedium",
        })}
      </CfcResultStatusRow>
      {resultAvailable && cfcReport?.id && (
        <CfcReportButton
          size="small"
          $backgroundColor="redMedium"
          $textColor="white"
          onClick={handleReportDownload}
        >
          <DetailedResultsViewReportButtonWrapper>
            <FontAwesomeIcon aria-hidden icon={faFilePdf} />
            {GetTypographyContent({
              content: t("cfc.actions.viewFullReport"),
              size: "medium",
              textColor: "white",
              isBold: true,
            })}
          </DetailedResultsViewReportButtonWrapper>
        </CfcReportButton>
      )}
    </CfcResultCard>
  );

  return (
    <CfcWrapper>
      <CfcContentWrapper>
        <CfcMainColumn>
          {pageHeader}
          {resultCard}
        </CfcMainColumn>
        <CfcSupportColumn>
          <CfcGeneralContainer>
            <DetailedResultsExpert />
          </CfcGeneralContainer>
          <CfcGeneralContainer>
            <DetailedResultsFAQ />
          </CfcGeneralContainer>
        </CfcSupportColumn>
      </CfcContentWrapper>
    </CfcWrapper>
  );
};

export default CfcPage;
